/**
 * EmptyState Component
 *
 * Premium empty states with clear CTAs and helpful guidance.
 * Used when lists are empty or data hasn't been loaded yet.
 */

import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import {
  FileX,
  Plus,
  Search,
  Inbox,
  FolderOpen,
  FileSearch,
  Sparkles,
  ArrowRight,
  CheckCircle2,
  AlertCircle,
  Clock,
  Zap,
  type LucideIcon,
} from 'lucide-react';

interface EmptyStateAction {
  label: string;
  onClick: () => void;
  variant?: 'default' | 'outline' | 'secondary' | 'ghost';
  icon?: LucideIcon;
}

interface EmptyStateProps {
  icon?: LucideIcon;
  title: string;
  description?: string;
  primaryAction?: EmptyStateAction;
  secondaryAction?: EmptyStateAction;
  tips?: string[];
  variant?: 'default' | 'search' | 'filter' | 'success' | 'welcome';
  className?: string;
}

const variantConfig = {
  default: {
    icon: Inbox,
    iconBg: 'bg-muted',
    iconColor: 'text-muted-foreground',
  },
  search: {
    icon: FileSearch,
    iconBg: 'bg-blue-100 dark:bg-blue-900/30',
    iconColor: 'text-blue-600 dark:text-blue-400',
  },
  filter: {
    icon: FolderOpen,
    iconBg: 'bg-amber-100 dark:bg-amber-900/30',
    iconColor: 'text-amber-600 dark:text-amber-400',
  },
  success: {
    icon: CheckCircle2,
    iconBg: 'bg-green-100 dark:bg-green-900/30',
    iconColor: 'text-green-600 dark:text-green-400',
  },
  welcome: {
    icon: Sparkles,
    iconBg: 'bg-primary/10',
    iconColor: 'text-primary',
  },
};

export function EmptyState({
  icon,
  title,
  description,
  primaryAction,
  secondaryAction,
  tips,
  variant = 'default',
  className,
}: EmptyStateProps) {
  const config = variantConfig[variant];
  const Icon = icon || config.icon;

  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center py-12 px-4 animate-fade-in',
        className
      )}
    >
      {/* Icon */}
      <div
        className={cn(
          'inline-flex items-center justify-center w-16 h-16 rounded-full mb-6',
          config.iconBg
        )}
      >
        <Icon className={cn('h-8 w-8', config.iconColor)} />
      </div>

      {/* Title */}
      <h3 className="text-lg font-semibold text-foreground mb-2">{title}</h3>

      {/* Description */}
      {description && (
        <p className="text-sm text-muted-foreground max-w-sm mb-6">
          {description}
        </p>
      )}

      {/* Actions */}
      {(primaryAction || secondaryAction) && (
        <div className="flex flex-wrap items-center justify-center gap-3 mb-6">
          {secondaryAction && (
            <Button
              variant={secondaryAction.variant || 'outline'}
              onClick={secondaryAction.onClick}
              className="transition-premium"
            >
              {secondaryAction.icon && (
                <secondaryAction.icon className="h-4 w-4 mr-2" />
              )}
              {secondaryAction.label}
            </Button>
          )}
          {primaryAction && (
            <Button
              variant={primaryAction.variant || 'default'}
              onClick={primaryAction.onClick}
              className="transition-premium"
            >
              {primaryAction.icon && (
                <primaryAction.icon className="h-4 w-4 mr-2" />
              )}
              {primaryAction.label}
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          )}
        </div>
      )}

      {/* Tips */}
      {tips && tips.length > 0 && (
        <div className="bg-muted/50 rounded-lg p-4 max-w-md">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider mb-2">
            Quick Tips
          </p>
          <ul className="text-sm text-muted-foreground space-y-1">
            {tips.map((tip, i) => (
              <li key={i} className="flex items-start gap-2">
                <span className="text-primary">•</span>
                <span>{tip}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * WelcomeEmptyState - Special empty state for first-time users
 */
interface WelcomeEmptyStateProps {
  userName?: string;
  onStartWizard: () => void;
  onQuickCreate: () => void;
  className?: string;
}

export function WelcomeEmptyState({
  userName,
  onStartWizard,
  onQuickCreate,
  className,
}: WelcomeEmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center py-16 px-4 animate-fade-in',
        className
      )}
    >
      {/* Welcome animation */}
      <div className="relative mb-8">
        <div className="absolute inset-0 bg-primary/20 rounded-full blur-xl animate-pulse" />
        <div className="relative inline-flex items-center justify-center w-20 h-20 rounded-full bg-primary/10 border-2 border-primary/20">
          <Sparkles className="h-10 w-10 text-primary animate-celebration" />
        </div>
      </div>

      {/* Greeting */}
      <h2 className="text-2xl font-bold text-foreground mb-2">
        {userName ? `Welcome, ${userName}!` : 'Welcome to Normiz!'}
      </h2>
      <p className="text-muted-foreground max-w-md mb-8">
        Your permit operations center is ready. Let's get your first permit
        application started.
      </p>

      {/* Feature highlights */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-2xl mb-8">
        <div className="flex flex-col items-center p-4 rounded-lg bg-muted/30">
          <CheckCircle2 className="h-6 w-6 text-green-600 mb-2" />
          <span className="text-sm font-medium">Compliance Tracking</span>
          <span className="text-xs text-muted-foreground">
            Know what's needed
          </span>
        </div>
        <div className="flex flex-col items-center p-4 rounded-lg bg-muted/30">
          <Clock className="h-6 w-6 text-blue-600 mb-2" />
          <span className="text-sm font-medium">Smart Guidance</span>
          <span className="text-xs text-muted-foreground">
            Step-by-step help
          </span>
        </div>
        <div className="flex flex-col items-center p-4 rounded-lg bg-muted/30">
          <Zap className="h-6 w-6 text-amber-600 mb-2" />
          <span className="text-sm font-medium">Fast Processing</span>
          <span className="text-xs text-muted-foreground">
            Avoid rejections
          </span>
        </div>
      </div>

      {/* Actions */}
      <div className="flex flex-col sm:flex-row items-center gap-3">
        <Button variant="outline" onClick={onQuickCreate} className="w-full sm:w-auto">
          <Zap className="h-4 w-4 mr-2" />
          Quick Create
        </Button>
        <Button onClick={onStartWizard} size="lg" className="w-full sm:w-auto">
          <Plus className="h-4 w-4 mr-2" />
          Start Guided Intake
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      </div>

      {/* Subtle hint */}
      <p className="text-xs text-muted-foreground mt-6">
        The guided intake walks you through every step - perfect for your first permit
      </p>
    </div>
  );
}

/**
 * FilteredEmptyState - When a filter returns no results
 */
interface FilteredEmptyStateProps {
  filterName: string;
  filterValue: string;
  onClearFilter: () => void;
  className?: string;
}

export function FilteredEmptyState({
  filterName,
  filterValue,
  onClearFilter,
  className,
}: FilteredEmptyStateProps) {
  return (
    <EmptyState
      variant="filter"
      title={`No ${filterValue.toLowerCase()} permits`}
      description={`There are no permits with ${filterName.toLowerCase()} status "${filterValue.toLowerCase()}". This is good news if you're looking at blocked or review!`}
      primaryAction={{
        label: 'Clear Filter',
        onClick: onClearFilter,
        variant: 'outline',
      }}
      className={className}
    />
  );
}

/**
 * SearchEmptyState - When a search returns no results
 */
interface SearchEmptyStateProps {
  query: string;
  onClear: () => void;
  className?: string;
}

export function SearchEmptyState({
  query,
  onClear,
  className,
}: SearchEmptyStateProps) {
  return (
    <EmptyState
      variant="search"
      icon={Search}
      title="No results found"
      description={`We couldn't find any permits matching "${query}". Try adjusting your search terms or filters.`}
      primaryAction={{
        label: 'Clear Search',
        onClick: onClear,
        variant: 'outline',
      }}
      tips={[
        'Try searching by address or client name',
        'Use fewer words for broader results',
        'Check your filters aren\'t too restrictive',
      ]}
      className={className}
    />
  );
}

/**
 * SuccessEmptyState - When all items are completed
 */
interface SuccessEmptyStateProps {
  title?: string;
  description?: string;
  onViewAll?: () => void;
  className?: string;
}

export function SuccessEmptyState({
  title = 'All caught up!',
  description = 'There are no items requiring your attention right now.',
  onViewAll,
  className,
}: SuccessEmptyStateProps) {
  return (
    <div
      className={cn(
        'flex flex-col items-center justify-center text-center py-12 px-4 animate-scale-in',
        className
      )}
    >
      <div className="relative mb-6">
        <div className="absolute inset-0 bg-green-500/20 rounded-full blur-lg animate-pulse" />
        <div className="relative inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-100 dark:bg-green-900/30 glow-success">
          <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
        </div>
      </div>

      <h3 className="text-lg font-semibold text-foreground mb-2">{title}</h3>
      <p className="text-sm text-muted-foreground max-w-sm mb-4">
        {description}
      </p>

      {onViewAll && (
        <Button variant="ghost" onClick={onViewAll}>
          View All Permits
          <ArrowRight className="h-4 w-4 ml-2" />
        </Button>
      )}
    </div>
  );
}

export default EmptyState;
